import type { InputHTMLAttributes, ReactNode } from 'react';
import { useState } from 'react';
import { CircleAlert, Eye, EyeOff, Loader2 } from 'lucide-react';

/**
 * The auth screens are plain elements rather than the shadcn Input/Button: those
 * carry a 40px height and a two-ring focus treatment, and dressing them up to
 * look like this design meant overriding nearly every class they set. Nothing
 * else in the product uses these.
 *
 * They used to hard-code a light-only palette. Everything here is a token now,
 * so the sign-in screen follows the theme like the rest of the console.
 */

/**
 * Accent blue as text. The accent is the same in both themes, which is right for a
 * fill but is only about 3.5:1 against the dark panel, under the 4.5:1 that text this
 * size needs. In dark mode the text takes a lighter blue (about 8:1); fills, borders
 * and the light theme keep the accent itself.
 */
export const ACCENT_TEXT = 'text-accent dark:text-[#8FA8FF]';

const LINK_CLASS = `text-body leading-none ${ACCENT_TEXT} no-underline hover:underline hover:underline-offset-4`;

type AuthHeadingProps = { title: string; subtitle: string };

/**
 * h1, not h2. The screen's own name is the page's heading; the chat beside it is a
 * promotional block and takes h2. Getting this the other way round left the
 * mobile layout with no h1 at all.
 */
export const AuthHeading = ({ title, subtitle }: AuthHeadingProps) => (
  <div className="flex flex-col gap-2">
    <h1 className="text-foreground m-0 text-[clamp(30px,3.4vw,38px)] leading-[1.05] font-medium tracking-[-0.035em]">
      {title}
    </h1>
    <p className="text-dim m-0 text-body leading-[1.5]">{subtitle}</p>
  </div>
);

type AuthErrorProps = { children: ReactNode };

/**
 * Sits above the field stack rather than under the offending input: on submit the
 * failure is usually about the pair, not about one box, and a message under the
 * password field is the one place a browser password manager likes to cover.
 */
export const AuthError = ({ children }: AuthErrorProps) => (
  <div
    role="alert"
    className="border-danger-line bg-danger-bg text-danger rounded-lg border px-[14px] py-3 text-body leading-[1.45]"
  >
    {children}
  </div>
);

type AuthNoticeProps = { title: string; children: ReactNode; tone?: 'ok' | 'warn' };

/**
 * The success counterpart of AuthError: a sent email, a completed reset. `warn` is for
 * news that is not a success but not a failure of the form either (a reset that saved
 * the password on an account that cannot sign in yet): a full hairline, a wash and an
 * icon, like the other warning notices, never a coloured left rule.
 */
export const AuthNotice = ({ title, children, tone = 'ok' }: AuthNoticeProps) =>
  tone === 'warn' ? (
    <div role="status" className="rounded-card border-warn-line bg-warn-bg flex gap-2.5 border px-[13px] py-3">
      <CircleAlert aria-hidden className="text-warn mt-px size-4 shrink-0" />
      <div className="flex flex-col gap-1">
        <span className="text-warn text-meta font-medium">{title}</span>
        <span className="text-foreground text-micro leading-[1.5]">{children}</span>
      </div>
    </div>
  ) : (
    <div role="status" className="rounded-card border-ok-line bg-ok-bg flex flex-col gap-1 border px-[13px] py-3">
      <span className="text-ok text-meta font-medium">{title}</span>
      <span className="text-foreground text-micro leading-[1.5]">{children}</span>
    </div>
  );

type AuthFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  name: string;
  /** Rendered on the label's baseline, opposite the label. "Forgot?" and friends. */
  action?: ReactNode;
  /** Shown under the input in the danger colour, and marks it invalid. */
  error?: string;
  /** Shown under the input in the dim colour while there is no error. */
  hint?: string;
  /** Rendered inside the input's right edge. The password field's eye toggle. */
  trailing?: ReactNode;
};

/**
 * The label is a sibling tied by htmlFor rather than a wrapper, so that `action`
 * sits on the label's baseline without joining the input's accessible name — a
 * password field announced as "Password Forgot?" is the cost of nesting it.
 */
export const AuthField = ({
  label,
  name,
  action,
  error,
  hint,
  trailing,
  className,
  id,
  ...inputProps
}: AuthFieldProps) => {
  const fieldId = id ?? `auth-${name}`;
  const noteId = `${fieldId}-note`;

  return (
    <div className="flex flex-col gap-[7px]">
      <div className="flex items-baseline justify-between">
        <label htmlFor={fieldId} className="micro-label" id={`${fieldId}-label`}>
          {label}
        </label>
        {action}
      </div>
      <div className="relative">
        <input
          id={fieldId}
          name={name}
          aria-invalid={!!error}
          aria-describedby={error || hint ? noteId : undefined}
          className={
            'border-line bg-field text-foreground placeholder:text-dim h-11 w-full rounded-lg border px-[13px] ' +
            'text-lede outline-none transition-colors duration-150 ' +
            'focus:border-accent focus:shadow-[0_0_0_3px_var(--accentLine)] ' +
            (error ? 'border-danger ' : '') +
            (trailing ? 'pr-[46px] ' : '') +
            (className ?? '')
          }
          {...inputProps}
        />
        {trailing && <div className="absolute top-1/2 right-1.5 -translate-y-1/2">{trailing}</div>}
      </div>
      {(error || hint) && (
        <span id={noteId} className={`text-micro leading-[1.4] ${error ? 'text-danger' : 'text-dim'}`}>
          {error || hint}
        </span>
      )}
    </div>
  );
};

type AuthPasswordFieldProps = Omit<AuthFieldProps, 'type' | 'trailing'>;

/**
 * Password input with a show/hide toggle. The toggle is a button in the tab order
 * with a state-bearing label, not a decoration: someone on a phone typing a
 * twelve-character password with a symbol in it needs to be able to check it.
 */
export const AuthPasswordField = (props: AuthPasswordFieldProps) => {
  const [shown, setShown] = useState(false);

  return (
    <AuthField
      {...props}
      type={shown ? 'text' : 'password'}
      trailing={
        <button
          type="button"
          onClick={() => setShown((current) => !current)}
          aria-label={shown ? 'Hide password' : 'Show password'}
          aria-pressed={shown}
          className="text-dim hover:bg-hover hover:text-foreground flex h-8 w-8 items-center justify-center rounded-[9px] transition-colors duration-150"
        >
          {shown ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
        </button>
      }
    />
  );
};

export const authLinkClass = LINK_CLASS;

type AuthButtonProps = {
  children: ReactNode;
  isPending?: boolean;
  disabled?: boolean;
  /** Before the label. */
  icon?: ReactNode;
  /** After the label. An arrow on a button that moves the visitor forward. */
  trailingIcon?: ReactNode;
  type?: 'button' | 'submit';
  onClick?: () => void;
  /** For the one button a screen exists to press, so Enter does it on arrival. */
  autoFocus?: boolean;
};

const BUTTON_BASE =
  'flex h-11 w-full items-center justify-center gap-2.5 rounded-lg text-lede font-medium leading-none ' +
  'transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60 ' +
  'focus-visible:outline-accent focus-visible:outline-2 focus-visible:outline-offset-2';

export const AuthPrimaryButton = ({
  children,
  isPending,
  disabled,
  icon,
  trailingIcon,
  type = 'submit',
  onClick,
  autoFocus,
}: AuthButtonProps) => (
  <button
    type={type}
    disabled={disabled}
    onClick={onClick}
    autoFocus={autoFocus}
    className={`${BUTTON_BASE} bg-cta hover:bg-ctahover disabled:hover:bg-cta text-white`}
  >
    {isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : icon}
    {children}
    {!isPending && trailingIcon}
  </button>
);

/** The outline button, for the action that is not the screen's main one. */
export const AuthSecondaryButton = ({
  children,
  icon,
  trailingIcon,
  isPending,
  disabled,
  type = 'button',
  onClick,
}: AuthButtonProps) => (
  <button
    type={type}
    disabled={disabled}
    onClick={onClick}
    className={`${BUTTON_BASE} border-line text-foreground hover:border-accent disabled:hover:border-line border bg-transparent`}
  >
    {isPending ? <Loader2 className="text-accent h-[18px] w-[18px] animate-spin" /> : icon}
    {children}
    {!isPending && trailingIcon}
  </button>
);

type AuthDividerProps = { label?: string };

export const AuthDivider = ({ label = 'or' }: AuthDividerProps) => (
  <div className="flex items-center gap-3.5 py-0.5">
    <div className="bg-line h-px flex-1" />
    <span className="micro-label">{label}</span>
    <div className="bg-line h-px flex-1" />
  </div>
);
